import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Form, Input, message, Progress, Upload } from "antd";
import TextArea from "antd/es/input/TextArea";
import React, { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  currentRole,
  generatePresignedUrl,
  getProjectId,
  getProjectInfoById,
  getProjectSpecificClientSiteListForUpload,
  userNameLocal,
} from "../../../../api/apiCallManager";
import { API_CONSTANTS } from "../../../../api/Constants";
import useProjectInfoQuery from "../../../../components/hooks/useProjectInfoQuery";
import Loader from "../../../../components/spinner/Loader";
import { showError, showSuccess } from "../../../../helpers/alerts";
import { GET_LOCAL_STORAGE } from "../../../../helpers/localStorage";
import { LOGGER } from "../../../../helpers/logger";
import useProfileDataStore from "../../../../zustand/store/profileDataStore";
import axios from "axios";

function UploadDataForm({ selectedEditData, closeModal }) {
  const [uploadProgress, setUploadProgress] = useState(0); // State for tracking upload progress
  const userProfile = useProfileDataStore((state) => state.userProfile);
  const { data: assignedClientList } = useQuery({
    queryKey: ["getProjectSpecificClientSiteListForUpload_"],
    queryFn: getProjectSpecificClientSiteListForUpload,
  });
  const triggerUploadRef = useRef(null);
  const [fileList, setFileList] = useState([]);
  const [loading, setLoading] = useState(false);
  const preSignedUrlRef = useRef("");

  const { id } = useParams();
  const {
    data: projectInfoData,
    error,
    isLoading,
  } = useProjectInfoQuery(id, getProjectInfoById);

  const presignedUrlMutation = useMutation({
    mutationFn: generatePresignedUrl,
    onSuccess: (data) => {
      if (data.statusCode == 200) {
        preSignedUrlRef.current = data.body;
      }
    },
  });

  const uploadFiles = async (formValues) => {
    const formData = new FormData();
    const files = fileList;
    files.forEach((file) => {
      formData.append("file", file);
    });

    const fileName = files.length > 0 ? files[0].name : "default_file_name";
    const projectId = selectedEditData?.projectId || getProjectId();
    const projectKey =
      selectedEditData?.projectKey || projectInfoData?.project_key;
    // selectedEditData?.region ||
    const userRegion = userProfile?.userRegion;
    const siteName = selectedEditData?.siteName || userProfile?.siteName;
    const dataSetName = formValues?.dataSetName;
    const dataSetDescription = formValues?.dataSetDescription;
    const assignedClientName =
      selectedEditData?.clientName || assignedClientList[0]?.clientName;

    // Function to check if all values are valid (non-falsy)
    const checkAllValues = (...values) =>
      values.every((value) => Boolean(value));

    // Check all required values
    const isValid = checkAllValues(
      projectId,
      projectKey,
      userRegion,
      siteName,
      dataSetName,
      dataSetDescription,
      assignedClientName
    );
    let presignedUrlData;
    const userEmail = GET_LOCAL_STORAGE("email");
    const preSignedUrlPayload = {
      projectKey: projectKey,
      siteName: siteName,
      projectId: projectId,
      dataSetName: dataSetName,
      fileName: fileName,
      region: userRegion,
      clientName: assignedClientName,
      event_type: selectedEditData?.record_id
        ? "pre_signed_url_update"
        : "pre_signed_url",
    };

    try {
      // Fetch Pre-Signed URL first
      presignedUrlData = await presignedUrlMutation.mutateAsync(
        preSignedUrlPayload
      );
      // console.log(fileList[0].type, "======>");
      if (presignedUrlData.statusCode !== 200)
        throw new Error("Failed to get presigned URL");

      // Upload to the Pre-Signed URL
      const uploadResponse = await axios.put(
        presignedUrlData.body,
        fileList[0],
        {
          headers: {
            "Content-Type": "application/zip",
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          },
        }
      );
      // const uploadResponse = await fetch(presignedUrlData.body, {
      //   method: "PUT",
      //   body: fileList[0],
      //   headers: {
      //     "Content-Type": "application/zip",
      //     // "Content-Type": fileList[0].type,
      //   },
      // });

      if (!uploadResponse.ok) throw new Error("File upload failed");
    } catch (error) {
      // showError("An error occurred: " + error.message);
    }
    let payload;
    if (selectedEditData?.record_id) {
      payload = {
        record_id: selectedEditData.record_id,
        email: userEmail,
        projectId: projectId,
        projectKey: projectKey,
        fileName: fileName,
        region: userRegion,
        siteName: siteName,
        dataSetName: dataSetName,
        dataSetDescription: dataSetDescription,
        clientName: assignedClientName,
        role: currentRole,
        createdBy: userNameLocal,
        preSignedUrl: presignedUrlData?.body,
        message: `Dataset(name:${dataSetName}) Updated for ${siteName}(site) Successfully`,
        notificationLog: {
          title: "Dataset Updated",
          project_key_value: projectKey,
          description: `Dataset(name:${dataSetName}) Updated for ${siteName}(site) Successfully`,
          notificationType: "email",
          region: userRegion,
          notificationTo: null,
          createdBy: {
            role: "Site_Manager",
            email: userEmail,
          },
        },
      };
      // formData.append("log", JSON.stringify(logJson));
    } else {
      payload = {
        email: userEmail,
        projectId: projectId,
        projectKey: projectKey,
        fileName: fileName,
        region: userRegion,
        siteName: siteName,
        dataSetName: dataSetName,
        dataSetDescription: dataSetDescription,
        clientName: assignedClientName,
        role: currentRole,
        createdBy: userNameLocal,
        preSignedUrl: presignedUrlData?.body,
        message: `Dataset(name:${dataSetName}) Uploaded for ${siteName}(site) Successfully`,
        notificationLog: {
          title: "Dataset Uploaded",
          project_key_value: projectKey,
          description: `Dataset(name:${dataSetName}) Updated for ${siteName}(site) Successfully`,
          notificationType: "email",
          region: userRegion,
          notificationTo: null,
          createdBy: {
            role: "Site_Manager",
            email: userEmail,
          },
        },
      };
    }
    if (isValid) {
      const response = await axios.post(
        `${API_CONSTANTS.UPLOAD_BASE_URL}upload`,
        payload
      );
      if (response.status === 200) {
        return response.data;
      } else {
        throw new Error("Upload failed");
      }
    } else {
      showError("value not found");
    }
  };
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: uploadFiles,
    onSuccess: (data) => {
      setFileList([]);
      showSuccess("Uploaded successfully.");
      queryClient.invalidateQueries({ queryKey: ["_filesList"] });
      setLoading(false);
      closeModal();
    },
    onError: () => {
      setLoading(false);
    },
  });

  const handleUpload = (values) => {
    mutation.mutate(values);
  };

  const props = {
    onRemove: (file) => {
      // const index = fileList.indexOf(file);
      // const newFileList = fileList.slice();
      // newFileList.splice(index, 1);
      // setFileList(newFileList);
      setFileList([]);
    },
    beforeUpload: (file) => {
      const isValidType =
        // file.type === "text/csv" ||
        // file.type === "application/json" ||
        file.type === "application/zip" || file.name.endsWith(".zip"); // for ZIP files
      if (!isValidType) {
        message.error("You can only upload ZIP files!");
        return Upload.LIST_IGNORE;
      } else {
        setFileList([file]);
      }
      return false;
    },
    fileList,
    accept: ".zip", // Accept only .zip (other types:.doc, .docx, and .pdf files .pdf,.json)
    multiple: false,
  };

  const onTriggerUpload = () => {
    triggerUploadRef.current?.click();
  };
  const onFinish = (values) => {
    setLoading(true);
    handleUpload(values);
    LOGGER(values);
  };

  return (
    <div className="p-2">
      {/* {JSON.stringify(assignedClientList[0]?.clientName)}====== */}
      {/* {JSON.stringify(uploadProgress)} */}
      <Form
        onFinish={onFinish}
        layout="vertical"
        requiredMark={false}
        initialValues={{
          dataSetName: selectedEditData?.dataSetName || "",
          dataSetDescription: selectedEditData?.dataSetDescription || "",
        }}
      >
        <Form.Item
          name="dataSetName"
          label="Dataset Name"
          rules={[
            { required: true, message: "Please enter the dataset name" },
            {
              pattern: /^[a-zA-Z0-9_-]*$/,
              message:
                "Only letters, numbers, underscores, and hyphens are allowed. Spaces and other special characters are not allowed.",
            },
          ]}
        >
          <Input
            placeholder="Enter Dataset Name"
            disabled={selectedEditData?.dataSetName}
          />
        </Form.Item>

        <Form.Item
          name="dataSetDescription"
          label="Description"
          rules={[{ required: true, message: "Please enter a description" }]}
        >
          <TextArea rows={4} placeholder="Enter Description" />
        </Form.Item>
        <div
          onClick={onTriggerUpload}
          className="bg-[#f7f7f7] flex justify-center mt-9 py-6 cursor-pointer w-[100%] border-[1px] rounded-sm border-[#d9d9d9]"
        >
          <div className="text-center">
            <h3>Select the File </h3>
            <small>Supported Format: .ZIP</small>
          </div>
        </div>

        <Form.Item
          name="upload"
          label=""
          valuePropName="fileList"
          getValueFromEvent={({ fileList }) => fileList}
          rules={[{ required: true, message: "Please upload a file" }]}
        >
          <Upload {...props} maxCount={1}>
            <div ref={triggerUploadRef}></div>
          </Upload>
        </Form.Item>
        <div className="flex justify-center">
          <button
            disabled={loading}
            type="submit"
            className="w-[300px] rounded-[2px] flex justify-center items-center text-[13px] font-medium text-white py-[10px] bg-[#005EB8]"
          >
            <Loader isLoading={loading} />
            Submit
          </button>
        </div>
      </Form>

      <div className="my-2">
        {loading && (
          <Progress
            percent={uploadProgress}
            showInfo={true}
            status="active"
            strokeColor={"#005eb8"}
            className=" text-[12px] font-semibold"
          />
        )}
      </div>

      {/* <Button
        type="primary"
        onClick={handleUpload}
        disabled={fileList.length === 0}
        loading={mutation.isLoading}
      >
        {mutation.isLoading ? "Uploading" : "Start Upload"}
      </Button> */}
    </div>
  );
}

export default UploadDataForm;
